import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, API, useAuth } from "../lib/api";
import { MapContainer, TileLayer, Marker, Polyline, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  Map as MapIcon, MapPin, Anchor, Edit3, Trash2, Plus, X, Camera, Play, Pause, Loader2, Sparkles,
} from "lucide-react";

// Default modal-open coords (Göcek marina) — auto-geocode only overrides when values match this.
const DEFAULT_LAT = 36.74;
const DEFAULT_LNG = 28.94;

function authedThumb(itemId) {
  const token = localStorage.getItem("tk_token") || "";
  return `${API}/album/file/${itemId}?auth=${encodeURIComponent(token)}`;
}

function pinIcon(day, color) {
  return L.divIcon({
    className: "rota-pin",
    html: `<div style="position:relative;width:36px;height:36px;transform:translate(-50%,-100%);">
      <div style="position:absolute;inset:0;background:white;border-radius:50%;box-shadow:0 2px 6px rgba(0,0,0,0.35);"></div>
      <div style="position:absolute;inset:3px;background:${color};border-radius:50%;display:flex;align-items:center;justify-content:center;color:white;font-weight:700;font-family:Outfit,sans-serif;font-size:14px;">${day}</div>
    </div>`,
    iconSize: [36, 36],
    iconAnchor: [18, 36],
  });
}

const boatIcon = L.divIcon({
  className: "rota-boat",
  html: `<div style="font-size:26px;filter:drop-shadow(0 1px 3px rgba(0,0,0,0.4));transform:translate(-50%,-50%);">⛵</div>`,
  iconSize: [30, 30],
  iconAnchor: [15, 15],
});

function FitBounds({ stops }) {
  const map = useMap();
  useEffect(() => {
    if (!stops?.length) return;
    if (stops.length === 1) {
      map.setView([stops[0].lat, stops[0].lng], 12);
      return;
    }
    const bounds = L.latLngBounds(stops.map((s) => [s.lat, s.lng]));
    map.fitBounds(bounds, { padding: [40, 40] });
  }, [stops, map]);
  return null;
}

function ContextMenuAdd({ enabled, onAdd }) {
  useMapEvents({
    contextmenu(e) {
      if (!enabled) return;
      onAdd(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

export default function Rota() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [stops, setStops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeStop, setActiveStop] = useState(null);
  const [editing, setEditing] = useState(null);
  const [albumByDay, setAlbumByDay] = useState({});
  const [photoLoadingDay, setPhotoLoadingDay] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [boatProgress, setBoatProgress] = useState(0);
  const rafRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/trip-stops");
      setStops(Array.isArray(data) ? data : []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!playing || stops.length < 2) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      setBoatProgress(0);
      return undefined;
    }
    const durationMs = 8000;
    let started = null;
    const step = (t) => {
      if (!started) started = t;
      const r = Math.min(1, (t - started) / durationMs);
      setBoatProgress(r);
      if (r < 1) rafRef.current = requestAnimationFrame(step);
      else setPlaying(false);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [playing, stops.length]);

  const boatLatLng = useMemo(() => {
    if (stops.length < 2 || boatProgress === 0) return null;
    // Piecewise linear along the polyline
    const segs = [];
    let total = 0;
    for (let i = 0; i < stops.length - 1; i++) {
      const a = stops[i]; const b = stops[i + 1];
      const dLat = b.lat - a.lat; const dLng = b.lng - a.lng;
      const len = Math.sqrt(dLat * dLat + dLng * dLng);
      total += len;
      segs.push({ a, b, len, cum: total });
    }
    if (total === 0) return null;
    const target = boatProgress * total;
    const s = segs.find((sg) => sg.cum >= target) || segs[segs.length - 1];
    const localStart = s.cum - s.len;
    const local = s.len === 0 ? 0 : (target - localStart) / s.len;
    return [s.a.lat + (s.b.lat - s.a.lat) * local, s.a.lng + (s.b.lng - s.a.lng) * local];
  }, [stops, boatProgress]);

  const save = async (s) => {
    try {
      const payload = {
        day: Number(s.day),
        name: s.name,
        lat: Number(s.lat),
        lng: Number(s.lng),
        description: s.description || "",
        arrival: s.arrival || "",
        departure: s.departure || "",
      };
      if (s.id) await api.put(`/trip-stops/${s.id}`, payload);
      else await api.post("/trip-stops", payload);
      setEditing(null);
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || "Kaydedilemedi");
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`"${s.name}" silinsin mi?`)) return;
    try {
      await api.delete(`/trip-stops/${s.id}`);
      await load();
      if (activeStop?.id === s.id) setActiveStop(null);
    } catch (e) {
      alert(e.response?.data?.detail || "Silinemedi");
    }
  };

  const loadPhotosForDay = async (day) => {
    if (albumByDay[day]) return;
    setPhotoLoadingDay(day);
    try {
      const { data } = await api.get("/album", { params: { person: `day_${day}` } });
      setAlbumByDay((a) => ({ ...a, [day]: Array.isArray(data) ? data : [] }));
    } catch {
      setAlbumByDay((a) => ({ ...a, [day]: [] }));
    } finally {
      setPhotoLoadingDay(null);
    }
  };

  const addStopAtLatLng = (lat, lng) => {
    setEditing({ create: true, day: (stops.length || 0) + 1, name: "", lat, lng });
  };

  const center = stops.length ? [stops[0].lat, stops[0].lng] : [DEFAULT_LAT, DEFAULT_LNG];

  return (
    <div className="space-y-4" data-testid="rota-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-display font-semibold flex items-center gap-2">
            <MapIcon className="w-5 h-5" style={{ color: "#4992ff" }} />
            Seyahat Rotası
          </h2>
          <p className="text-sm" style={{ color: "hsl(var(--text-2))" }}>
            Göcek başlangıç/bitiş — tüm duraklar ve günlük plan.
            {isAdmin && <span className="ml-1 text-[11px] opacity-80">· Haritada sağ tıklayarak yeni durak ekle</span>}
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setPlaying((p) => !p)} className="tk-btn-ghost flex items-center gap-1.5" data-testid="rota-play-btn">
            {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            {playing ? "Durdur" : "Animasyon"}
          </button>
          {isAdmin && (
            <button
              onClick={() => setEditing({ create: true, day: (stops.length || 0) + 1, name: "", lat: DEFAULT_LAT, lng: DEFAULT_LNG })}
              className="tk-btn flex items-center gap-1.5"
              data-testid="rota-add-btn"
            >
              <Plus className="w-4 h-4" /> Yeni Durak
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="tk-card p-6 flex items-center gap-2 text-sm" style={{ color: "hsl(var(--text-2))" }}>
          <Loader2 className="w-4 h-4 animate-spin" /> Yükleniyor...
        </div>
      ) : (
        <div className="tk-card overflow-hidden p-0 relative" style={{ height: 460 }} data-testid="rota-map-container">
          <MapContainer center={center} zoom={11} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FitBounds stops={stops} />
            <ContextMenuAdd enabled={isAdmin} onAdd={addStopAtLatLng} />
            {stops.length > 1 && (
              <Polyline
                positions={stops.map((s) => [s.lat, s.lng])}
                pathOptions={{ color: "#4992ff", weight: 3.5, opacity: 0.9, dashArray: "9 6" }}
              />
            )}
            {stops.map((s, idx) => {
              const isStart = idx === 0;
              const isEnd = idx === stops.length - 1;
              const color = isStart ? "#16a34a" : isEnd ? "#dc2626" : "#4992ff";
              return (
                <Marker
                  key={s.id}
                  position={[s.lat, s.lng]}
                  icon={pinIcon(s.day, color)}
                  eventHandlers={{ click: () => { setActiveStop(s); loadPhotosForDay(s.day); } }}
                />
              );
            })}
            {boatLatLng && <Marker position={boatLatLng} icon={boatIcon} interactive={false} />}
          </MapContainer>
        </div>
      )}

      {activeStop && (
        <div className="tk-card p-4" data-testid="rota-active-stop">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-widest font-semibold" style={{ color: "#4992ff" }}>Gün {activeStop.day}</div>
              <div className="text-lg font-display font-semibold">{activeStop.name}</div>
              {activeStop.description && <p className="text-sm mt-1" style={{ color: "hsl(var(--text-2))" }}>{activeStop.description}</p>}
              {(activeStop.arrival || activeStop.departure) && (
                <p className="text-xs mt-1" style={{ color: "hsl(var(--text-2))" }}>
                  {activeStop.arrival && <>Varış: <b>{activeStop.arrival}</b></>}{activeStop.arrival && activeStop.departure ? " · " : ""}{activeStop.departure && <>Kalkış: <b>{activeStop.departure}</b></>}
                </p>
              )}
              <div className="text-[11px] mt-1" style={{ color: "hsl(var(--text-2))" }}>
                <MapPin className="inline w-3 h-3" /> {activeStop.lat.toFixed(4)}, {activeStop.lng.toFixed(4)}
              </div>
            </div>
            <button onClick={() => setActiveStop(null)} className="p-1.5 rounded-md hover:bg-[hsl(var(--surface-2))]"><X className="w-4 h-4" /></button>
          </div>
          <div className="mt-3">
            {photoLoadingDay === activeStop.day ? (
              <div className="text-xs flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Fotoğraflar yükleniyor...</div>
            ) : albumByDay[activeStop.day]?.length > 0 ? (
              <div className="grid grid-cols-4 md:grid-cols-6 gap-1.5">
                {albumByDay[activeStop.day].map((p) => (
                  <img key={p.id} src={authedThumb(p.id)} alt={p.original_filename} className="w-full rounded" style={{ aspectRatio: "1/1", objectFit: "cover" }} />
                ))}
              </div>
            ) : (
              <p className="text-[12px]" style={{ color: "hsl(var(--text-2))" }}>
                <Camera className="inline w-3.5 h-3.5" /> Bu güne ait foto eklemek için Albüm'de fotoğrafı <b>"day_{activeStop.day}"</b> kişi-etiketiyle yükle.
              </p>
            )}
          </div>
          {isAdmin && (
            <div className="flex gap-2 mt-3">
              <button onClick={() => setEditing(activeStop)} className="tk-btn-ghost text-sm flex items-center gap-1"><Edit3 className="w-4 h-4" /> Düzenle</button>
              <button onClick={() => remove(activeStop)} className="tk-btn-danger text-sm flex items-center gap-1"><Trash2 className="w-4 h-4" /> Sil</button>
            </div>
          )}
        </div>
      )}

      <div className="tk-card p-4" data-testid="rota-list">
        <div className="flex items-center gap-2 mb-3">
          <Anchor className="w-4 h-4" style={{ color: "#4992ff" }} />
          <div className="font-display font-semibold">Günlük Plan</div>
        </div>
        <ol className="space-y-2">
          {stops.map((s, idx) => (
            <li
              key={s.id}
              className="flex items-start gap-3 p-2 rounded-lg cursor-pointer hover:bg-[hsl(var(--surface-2))]"
              onClick={() => { setActiveStop(s); loadPhotosForDay(s.day); }}
              data-testid={`rota-item-${s.id}`}
            >
              <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 font-semibold text-xs"
                style={{ backgroundColor: idx === 0 ? "#16a34a" : idx === stops.length - 1 ? "#dc2626" : "#4992ff", color: "white" }}>
                {s.day}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm"><b>{s.name}</b></div>
                {s.description && <div className="text-[11px]" style={{ color: "hsl(var(--text-2))" }}>{s.description}</div>}
              </div>
              <div className="text-[10px]" style={{ color: "hsl(var(--text-2))" }}>
                <MapPin className="inline w-3 h-3" /> {s.lat.toFixed(3)}, {s.lng.toFixed(3)}
              </div>
            </li>
          ))}
        </ol>
      </div>

      {editing && (
        <EditStopModal stop={editing} onClose={() => setEditing(null)} onSave={save} />
      )}
    </div>
  );
}

function EditStopModal({ stop, onClose, onSave }) {
  const [s, setS] = useState({
    id: stop.id || "",
    day: stop.day || 1,
    name: stop.name || "",
    lat: stop.lat ?? DEFAULT_LAT,
    lng: stop.lng ?? DEFAULT_LNG,
    description: stop.description || "",
    arrival: stop.arrival || "",
    departure: stop.departure || "",
  });
  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));

  const [searchQ, setSearchQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [autoInfo, setAutoInfo] = useState(null); // {label} shown after auto-geocode

  const searchPlaces = async (query) => {
    const q = (query || searchQ).trim();
    if (!q) return null;
    try {
      const r = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&accept-language=tr&q=${encodeURIComponent(q + ", Türkiye")}&limit=6`,
        { headers: { "Accept-Language": "tr" } }
      );
      const j = await r.json();
      return Array.isArray(j) ? j : [];
    } catch {
      return [];
    }
  };

  const runSearch = async () => {
    if (!searchQ.trim()) return;
    setSearching(true);
    setResults(await searchPlaces());
    setSearching(false);
  };

  const pick = (r) => {
    setS((x) => ({
      ...x,
      lat: parseFloat(r.lat),
      lng: parseFloat(r.lon),
      name: x.name || (r.display_name || "").split(",").slice(0, 2).join(",").trim(),
    }));
    setResults([]);
    setSearchQ("");
    setAutoInfo({ label: r.display_name });
  };

  // Auto-geocode when the user types "Ad" and blurs, but ONLY if lat/lng are
  // still at the default Göcek fallback (never overwrite manual/picked coords).
  const isAtDefault = () => Math.abs(Number(s.lat) - DEFAULT_LAT) < 0.0001 && Math.abs(Number(s.lng) - DEFAULT_LNG) < 0.0001;
  const onNameBlur = async () => {
    if (!s.name || !s.name.trim()) return;
    if (!isAtDefault()) return; // manual coords already set
    const list = await searchPlaces(s.name);
    if (list && list.length > 0) {
      const top = list[0];
      setS((x) => ({ ...x, lat: parseFloat(top.lat), lng: parseFloat(top.lon) }));
      setAutoInfo({ label: top.display_name });
    }
  };

  const clearAuto = () => {
    setS((x) => ({ ...x, lat: DEFAULT_LAT, lng: DEFAULT_LNG }));
    setAutoInfo(null);
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-3" style={{ backgroundColor: "rgba(0,0,0,0.55)" }} onClick={onClose} data-testid="stop-edit-modal">
      <div className="tk-card p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="font-display font-semibold">{s.id ? "Durak Düzenle" : "Yeni Durak"}</div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-[hsl(var(--surface-2))]"><X className="w-4 h-4" /></button>
        </div>

        {/* Nominatim search */}
        <div className="mb-3">
          <label className="tk-label">Yer Ara (OpenStreetMap)</label>
          <div className="flex gap-1.5">
            <input
              className="tk-input flex-1"
              placeholder="örn. Tersane Adası, Göcek"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); runSearch(); } }}
              data-testid="nominatim-input"
            />
            <button onClick={runSearch} disabled={searching} className="tk-btn !py-1.5 !px-3 text-sm disabled:opacity-50" data-testid="nominatim-search-btn">
              {searching ? "..." : "Ara"}
            </button>
          </div>
          {results.length > 0 && (
            <ul className="mt-1 border rounded-md divide-y max-h-40 overflow-y-auto" style={{ borderColor: "hsl(var(--border))" }} data-testid="nominatim-results">
              {results.map((r) => (
                <li key={r.place_id}>
                  <button
                    type="button"
                    onClick={() => pick(r)}
                    className="w-full text-left px-2.5 py-1.5 hover:bg-[hsl(var(--surface-2))] text-xs"
                    data-testid={`nominatim-result-${r.place_id}`}
                  >
                    {r.display_name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="tk-label">Gün</label>
            <input type="number" className="tk-input" value={s.day} onChange={(e) => set("day", e.target.value)} data-testid="stop-day-input" />
          </div>
          <div>
            <label className="tk-label">Ad</label>
            <input
              className="tk-input"
              value={s.name}
              onChange={(e) => { set("name", e.target.value); if (autoInfo) setAutoInfo(null); }}
              onBlur={onNameBlur}
              data-testid="stop-name-input"
            />
          </div>
          <div>
            <label className="tk-label">Enlem (lat)</label>
            <input type="number" step="0.0001" className="tk-input" value={s.lat} onChange={(e) => { set("lat", e.target.value); setAutoInfo(null); }} data-testid="stop-lat-input" />
          </div>
          <div>
            <label className="tk-label">Boylam (lng)</label>
            <input type="number" step="0.0001" className="tk-input" value={s.lng} onChange={(e) => { set("lng", e.target.value); setAutoInfo(null); }} data-testid="stop-lng-input" />
          </div>
          {autoInfo && (
            <div className="col-span-2 flex items-start gap-2 text-[11px] p-2 rounded-md" style={{ backgroundColor: "rgba(73,146,255,0.10)", color: "#4992ff" }} data-testid="auto-geocode-badge">
              <Sparkles className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              <span className="flex-1 truncate">Otomatik konum: {autoInfo.label}</span>
              <button type="button" onClick={clearAuto} className="hover:underline">Geri al</button>
            </div>
          )}
          <div className="col-span-2">
            <label className="tk-label">Açıklama</label>
            <input className="tk-input" value={s.description} onChange={(e) => set("description", e.target.value)} data-testid="stop-desc-input" />
          </div>
          <div>
            <label className="tk-label">Varış</label>
            <input className="tk-input" placeholder="örn. 14:00" value={s.arrival} onChange={(e) => set("arrival", e.target.value)} />
          </div>
          <div>
            <label className="tk-label">Kalkış</label>
            <input className="tk-input" placeholder="örn. 09:00" value={s.departure} onChange={(e) => set("departure", e.target.value)} />
          </div>
        </div>
        <div className="flex gap-2 justify-end mt-4">
          <button onClick={onClose} className="tk-btn-ghost text-sm">Vazgeç</button>
          <button onClick={() => onSave(s)} className="tk-btn text-sm" data-testid="stop-save-btn">Kaydet</button>
        </div>
      </div>
    </div>
  );
}
